import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@repo/ui/src/components/ui/drawer';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@repo/ui/src/components/ui/sheet';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import { cn } from '@repo/ui/src/lib/utils';

export interface ResponsiveSheetDiscardCopy {
  title: string;
  body: string;
  confirm: string;
  cancel: string;
}

interface ResponsiveSheetCloseContextValue {
  /** Same guarded close the backdrop tap / Escape / swipe-down already use
   *  internally (`guardClose(false)`): shows the discard confirm dialog when
   *  `dirty` and `discard` are set, otherwise closes immediately. Exposed so
   *  a button rendered in `footer` or `children` (e.g. a "Huỷ"/Cancel
   *  button) can request the SAME guarded close instead of calling the raw
   *  `onOpenChange` prop directly, which would bypass the guard. */
  requestClose: () => void;
}

const ResponsiveSheetCloseContext = createContext<ResponsiveSheetCloseContextValue | null>(null);

/** Read inside `footer` or `children` to request a guarded close (the same
 *  dirty-check the backdrop tap / Escape / swipe-down use). Throws outside a
 *  `ResponsiveSheet` — every consumer renders inside one. */
// eslint-disable-next-line react-refresh/only-export-components -- shared hook, not a component
export function useResponsiveSheetClose(): ResponsiveSheetCloseContextValue {
  const ctx = useContext(ResponsiveSheetCloseContext);
  if (!ctx) throw new Error('useResponsiveSheetClose must be used inside a ResponsiveSheet');
  return ctx;
}

/**
 * The one panel shell for the progress feature (spec §11.2.1): a vaul Drawer
 * from the bottom on phones, a Radix Sheet on the right on desktop — 480px by
 * default, 520px when a consumer passes `desktopWidth={520}` (THI-267's
 * class form, INVARIANTS §6.4/§6.7).
 *
 * Why not one component for both: the repo is on Radix, and a vaul drawer on a
 * desktop viewport stays a short bottom panel — unusable for the ten textareas
 * of D1 or the month editor of D3. Base UI's drawer would switch to a side
 * panel on its own; migrating to it is not this cycle's work.
 *
 * A project-wide audit of the remaining Sheet/Drawer call sites is a separate
 * Linear issue — do NOT convert unrelated panels to this wrapper here.
 *
 * Consumer contract: this component holds NO mutable draft state of its
 * own — `children` is fully controlled, and the caller must be the one
 * state owner for anything editable rendered inside (mirrors
 * SessionNotesPanel.tsx's own header comment: hooks belong to whichever
 * component's fiber owns them, not to whichever child tree currently
 * happens to be mounted). That, plus the branch latch below, is what keeps
 * a teacher's unsaved text alive across every event that could otherwise
 * swap which element type wraps `children`: `useIsMobile()` resolving
 * after mount, AND a live viewport change while the panel is open (window
 * resize across 768px, DevTools device toolbar) — either would flip
 * `<Sheet>` to `<Drawer>` or back, and React remounts an entire subtree
 * whenever its root element type changes.
 *
 * A button rendered in `footer` or `children` that wants to close the panel
 * (e.g. a "Huỷ"/Cancel button) must go through the SAME guard the backdrop
 * tap / Escape / swipe-down already use, not call the raw `onOpenChange`
 * prop directly — that would skip the dirty-check and discard confirmation.
 * Read `useResponsiveSheetClose().requestClose()` for this; it is provided
 * via context around both `children` and `footer` in each branch below.
 */
export function ResponsiveSheet({
  open, onOpenChange, a11yTitle, title, description, footer, dirty = false, discard, children,
  // THI-267 Task 11: the class form needs more width than the progress
  // feature's panels (five sections plus the per-day table a disclosure can
  // open — design spec S4's own note: "không phải Dialog 420 như 'Tạo slot'
  // vì form có năm mục và cần chỗ cho bảng ngày"). A closed union, not a
  // free number, so Tailwind's static class scanner can see both literal
  // widths at build time — an interpolated `w-[${n}px]` would compile to
  // nothing. Every other consumer omits this and keeps the original 480px.
  desktopWidth = 480,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  a11yTitle: string;
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  dirty?: boolean;
  discard?: ResponsiveSheetDiscardCopy;
  children: ReactNode;
  desktopWidth?: 480 | 520;
}) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // useIsMobile() coerces to a boolean and is FALSE on the first render
  // regardless of viewport (its matchMedia effect has not run yet), so
  // branching on "!isMobile" would render the Sheet first even on a phone.
  // `mounted` distinguishes "definitely desktop" from "not sure yet"; while
  // unsure the Drawer is what renders (port guide §2.6).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // The setState here is intentional: mount itself is the external signal.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
  }, []);
  const isMobile = useIsMobile();
  const isDesktopNow = mounted && !isMobile;

  // Latch which branch is rendered for the DURATION of one open: `open`
  // itself flipping false->true is the only thing allowed to re-decide
  // `lockedIsDesktop`; a live viewport change while `open` stays true must
  // not (that's the finding this fixes — useIsMobile() is a live
  // matchMedia hook, so a resize past 768px or the DevTools device
  // toolbar can flip `isDesktopNow` mid-session, and without this latch
  // that would swap <Sheet> for <Drawer> under a mounted `children`,
  // destroying whatever dirty draft it holds with no discard prompt in
  // the way — guardClose only guards the open->close transition, not a
  // live element-type change).
  //
  // `openEdge`/`lockedIsDesktop` use the React-documented "adjust state
  // while rendering" technique (comparing a prop to its own last-seen
  // value stored in state, then conditionally calling a setter in the
  // render body itself) rather than an effect: an effect only runs AFTER
  // the commit that shows the panel, which would let the stale branch
  // flash for one frame on every fresh open. Doing it inline means the
  // very first render where `open` is true already reads the freshly
  // relatched value. It's state, not a ref, because refs are not safe to
  // read or write during rendering (React may invoke a render function
  // more than once per commit).
  const [openEdge, setOpenEdge] = useState(open);
  const [lockedIsDesktop, setLockedIsDesktop] = useState(isDesktopNow);
  if (open !== openEdge) {
    setOpenEdge(open);
    if (open) setLockedIsDesktop(isDesktopNow);
  }

  // While closed there's nothing visible to protect, so this may as well
  // track the live value — the next real open (above) relatches anyway.
  const isDesktop = open ? lockedIsDesktop : isDesktopNow;

  const guardClose = (next: boolean) => {
    if (!next && dirty && discard) {
      setConfirmDiscard(true);
      return;
    }
    onOpenChange(next);
  };

  // Stable-shaped (not stable-reference, but cheap) value for the close
  // context — see useResponsiveSheetClose above.
  const closeContextValue: ResponsiveSheetCloseContextValue = { requestClose: () => guardClose(false) };

  const discardDialog = discard ? (
    // A JSX DESCENDANT of the panel content, never a sibling (INVARIANTS §6).
    <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{discard.title}</AlertDialogTitle>
          <AlertDialogDescription>{discard.body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{discard.cancel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => { setConfirmDiscard(false); onOpenChange(false); }}
          >
            {discard.confirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  ) : null;

  // D1's header: 12/16/8 padding, 8px gap, title 19px display, one muted line.
  const headerBody = (
    <div className="min-w-0 flex-1">
      <p className="font-display text-[19px] font-semibold tracking-[-0.015em] text-surface-900">{title}</p>
      {description && <p className="mt-0.5 text-[12.5px] text-surface-500">{description}</p>}
    </div>
  );

  if (isDesktop) {
    return (
      <Sheet open={open} onOpenChange={guardClose}>
        <SheetContent
          side="right"
          className={cn(
            'flex flex-col gap-0 p-0',
            desktopWidth === 520 ? 'w-[520px] sm:max-w-[520px]' : 'w-[480px] sm:max-w-[480px]',
          )}
        >
          {/* pr-10 clears SheetContent's own absolute Close at right-4 top-4 —
              the built-in X is kept on desktop (it is the accessible close),
              so the title must never sit under it. */}
          <SheetHeader className="flex-row items-start gap-2.5 px-5 pb-2.5 pr-10 pt-[12px] text-left">
            <SheetTitle className="sr-only">{a11yTitle}</SheetTitle>
            {headerBody}
          </SheetHeader>
          <ResponsiveSheetCloseContext.Provider value={closeContextValue}>
            {children}
            {footer}
          </ResponsiveSheetCloseContext.Provider>
          {discardDialog}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Drawer open={open} onOpenChange={guardClose} repositionInputs={false}>
      {/* max-h in dvh, not vh: the iOS URL bar makes vh lie. `repositionInputs`
          is off because vaul's default scroll-to-input jitters with ten
          textareas; the caller's body div owns the scroll instead — and vaul
          decides drag-vs-scroll by looking for a scrollable CHILD, so
          DrawerContent must never be that container (INVARIANTS §6). */}
      <DrawerContent className="flex max-h-[92dvh] flex-col">
        <DrawerHeader className="flex flex-row items-start gap-2.5 px-5 pb-2.5 pt-[12px] text-left">
          <DrawerTitle className="sr-only">{a11yTitle}</DrawerTitle>
          {headerBody}
          {/* Own X on phone, INSIDE the flex row, so it can never overlap the
              title the way an absolutely positioned one does (spec §11.2.2). */}
          <button
            type="button"
            aria-label={a11yTitle}
            onClick={() => guardClose(false)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm text-surface-500"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </DrawerHeader>
        <ResponsiveSheetCloseContext.Provider value={closeContextValue}>
          {children}
          {footer}
        </ResponsiveSheetCloseContext.Provider>
        {discardDialog}
      </DrawerContent>
    </Drawer>
  );
}
